/**
 * Reflection prompt — DEC-0014 blocking-inline with explicit Skip.
 *
 * Rendered after a passing module-quiz submit, in place of the usual
 * post-submit KC review. Two buttons:
 *
 * - **Save reflection** → ``POST /api/modules/{id}/reflection`` with body
 * - **Skip for now**    → same endpoint, body=null (FR-L-3 optional)
 *
 * Either response → parent's ``onDone()`` fires and the page navigates
 * to the next module (or stays put if this was the last module).
 *
 * Prompt TEXT is bundled into the build via ``import.meta.glob`` —
 * each module's ``content/module-N-<slug>/reflection-prompt.md`` is
 * read as raw markdown. We strip the YAML frontmatter and render the
 * body. Build-time resolution = zero round-trip to the backend.
 */
import * as React from "react";
import { useCallback, useState } from "react";

import { writeReflection } from "@/lib/module-api";

// Vite bundles every reflection-prompt.md under content/ as a raw string.
// Keys are relative to this file (``../../../content/...``). We index by
// module slug at render time.
const promptModules = import.meta.glob<string>(
  "../../../content/**/reflection-prompt.md",
  { query: "?raw", import: "default", eager: true },
);

function findPromptText(moduleSlug: string): string | null {
  // Match either ``content/<moduleSlug>/...`` or ``content/module-N-<moduleSlug>/...``
  // — Phase 0 uses the latter (e.g. ``module-1-cait-foundations``).
  for (const [key, raw] of Object.entries(promptModules)) {
    if (
      key.includes(`/${moduleSlug}/`) ||
      key.match(new RegExp(`/module-\\d+-${moduleSlug}/`))
    ) {
      // Vite ``?raw`` + ``import: "default"`` + ``eager: true`` SHOULD
      // yield the file content directly as a string, but in some build
      // matrices it lands as a ``{ default: "..." }`` module wrapper.
      // Unwrap defensively so a render crash here doesn't take the
      // whole reviewing branch down (the ``e.startsWith`` P0).
      const text =
        typeof raw === "string"
          ? raw
          : typeof (raw as { default?: unknown } | null)?.default === "string"
            ? ((raw as { default: string }).default)
            : null;
      if (text === null) return null;
      return stripFrontmatter(text);
    }
  }
  return null;
}

/** Strip a leading ``---\n...\n---\n`` YAML frontmatter block. */
function stripFrontmatter(md: string): string {
  if (typeof md !== "string") return "";
  if (!md.startsWith("---")) return md.trim();
  const end = md.indexOf("\n---", 3);
  if (end === -1) return md.trim();
  return md.slice(end + 4).trim();
}

export interface ReflectionPromptProps {
  moduleId: string;
  moduleSlug: string;
  moduleSequence: number;
  /** Fires after Save or Skip resolves. Parent navigates to next module. */
  onDone: () => void;
  /** Test seam. */
  writeFn?: typeof writeReflection;
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error"; message: string };

export function ReflectionPrompt({
  moduleId,
  moduleSlug,
  moduleSequence,
  onDone,
  writeFn = writeReflection,
}: ReflectionPromptProps): React.JSX.Element {
  const [body, setBody] = useState<string>("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  const promptText =
    findPromptText(moduleSlug) ??
    `What's one thing you'll take from Module ${moduleSequence} into your practice?`;

  const onSave = useCallback(async (): Promise<void> => {
    setState({ kind: "saving" });
    try {
      const value = body.trim().length === 0 ? null : body.trim();
      await writeFn(moduleId, value);
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not save.";
      setState({ kind: "error", message: msg });
    }
  }, [body, moduleId, onDone, writeFn]);

  const onSkip = useCallback(async (): Promise<void> => {
    setState({ kind: "saving" });
    try {
      await writeFn(moduleId, null);
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not skip.";
      setState({ kind: "error", message: msg });
    }
  }, [moduleId, onDone, writeFn]);

  const saving = state.kind === "saving";
  const errMsg = state.kind === "error" ? state.message : null;

  return (
    <section
      className="rounded-lg border border-border bg-white p-6"
      data-testid="reflection-prompt"
    >
      <h2 className="text-lg font-semibold text-foreground">
        Reflect on Module {moduleSequence}
      </h2>
      <p className="mt-3 whitespace-pre-line text-sm text-foreground">
        {promptText}
      </p>
      <p className="mt-3 text-xs text-muted-foreground">
        This reflection is private to you — it is not graded, and is not
        shared with anyone else.
      </p>
      <label className="mt-4 block">
        <span className="sr-only">Your reflection</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          disabled={saving}
          placeholder="Type as much or as little as you want…"
          className="w-full rounded-md border border-border p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </label>
      {errMsg !== null ? (
        <p className="mt-2 text-sm text-red-700" role="alert">
          {errMsg}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {saving ? "Saving…" : "Save reflection"}
        </button>
        <button
          type="button"
          onClick={() => {
            void onSkip();
          }}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          Skip for now
        </button>
      </div>
    </section>
  );
}
